import { useState, useRef } from "react";
import ImageField from "@/components/ImageField";
import { uploadFile, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";

const inputCls =
  "w-full bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors placeholder:text-zinc-600";
const labelCls = "block text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-2";

export default function BlacklistForm({ initial = {}, onSubmit, submitting, submitLabel = "Lưu" }) {
  const [form, setForm] = useState({
    customer_name: initial.customer_name || "",
    phone: initial.phone || "",
    dob: initial.dob || "",
    cccd_number: initial.cccd_number || "",
    license_number: initial.license_number || "",
    address: initial.address || "",
    reason: initial.reason || "",
    warning_level: initial.warning_level || "canh_bao",
  });
  const [images, setImages] = useState({
    cccd_front: initial.cccd_front || null,
    cccd_back: initial.cccd_back || null,
    license_image: initial.license_image || null,
  });
  const [related, setRelated] = useState(initial.related_images || []);
  const relRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setImg = (k) => (v) => setImages({ ...images, [k]: v });

  const addRelated = async (file) => {
    if (!file) return;
    try {
      const path = await uploadFile(file);
      setRelated((r) => [...r, path]);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, ...images, related_images: related });
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="bg-card border border-zinc-800 rounded-sm p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Tên khách hàng *</label>
            <input data-testid="rp-name" value={form.customer_name} onChange={set("customer_name")} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>Số điện thoại</label>
            <input data-testid="rp-phone" value={form.phone} onChange={set("phone")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Ngày tháng năm sinh</label>
            <input data-testid="rp-dob" value={form.dob} onChange={set("dob")} className={inputCls} placeholder="VD: 01/01/1990" />
          </div>
          <div>
            <label className={labelCls}>Số CCCD/CMND</label>
            <input data-testid="rp-cccd" value={form.cccd_number} onChange={set("cccd_number")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Số bằng lái</label>
            <input data-testid="rp-license" value={form.license_number} onChange={set("license_number")} className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Địa chỉ</label>
          <input data-testid="rp-address" value={form.address} onChange={set("address")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Mức độ cảnh báo *</label>
          <div className="flex gap-3">
            {[
              { v: "canh_bao", label: "Cảnh báo", cls: "border-amber-500/50 data-[on=true]:bg-amber-500/15 data-[on=true]:text-amber-400" },
              { v: "dac_biet_nguy_hiem", label: "Đặc biệt nguy hiểm", cls: "border-red-500/50 data-[on=true]:bg-red-500/20 data-[on=true]:text-red-400" },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                data-testid={`rp-level-${o.v}`}
                data-on={form.warning_level === o.v}
                onClick={() => setForm({ ...form, warning_level: o.v })}
                className={`flex-1 py-3 rounded-sm border text-sm font-bold transition-colors text-zinc-400 ${o.cls}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>Lý do báo cáo *</label>
          <textarea data-testid="rp-reason" value={form.reason} onChange={set("reason")} rows={4} className={inputCls} placeholder="Mô tả rõ hành vi vi phạm..." required />
        </div>
      </div>

      <div className="bg-card border border-zinc-800 rounded-sm p-6">
        <h2 className="font-display text-lg font-bold text-white mb-4">Hình ảnh CCCD / Bằng lái</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          <ImageField label="CCCD mặt trước" value={images.cccd_front} onChange={setImg("cccd_front")} testid="rp-img-front" />
          <ImageField label="CCCD mặt sau" value={images.cccd_back} onChange={setImg("cccd_back")} testid="rp-img-back" />
          <ImageField label="Bằng lái" value={images.license_image} onChange={setImg("license_image")} testid="rp-img-license" />
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <label className={labelCls + " mb-0"}>Tài liệu / hình ảnh liên quan</label>
            <button type="button" data-testid="rp-add-related" onClick={() => relRef.current?.click()} className="flex items-center gap-1 text-sm text-primary hover:text-[#ff1a1a] font-semibold">
              <Plus className="w-4 h-4" /> Thêm ảnh
            </button>
            <input ref={relRef} type="file" accept="image/*" className="hidden" onChange={(e) => addRelated(e.target.files?.[0])} />
          </div>
          {related.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {related.map((p, i) => (
                <div key={i} className="relative aspect-square border border-zinc-800 rounded-sm overflow-hidden">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setRelated(related.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/70 hover:bg-primary text-white p-1 rounded-sm transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <button
        data-testid="rp-submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-[#ff1a1a] text-white font-bold uppercase tracking-wide rounded-sm px-6 py-3 transition-colors disabled:opacity-50 shadow-[0_0_15px_rgba(230,0,0,0.3)]"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitLabel}
      </button>
    </form>
  );
}
